/**
 * Top-level SPA entry.
 *
 * Mounts BrowserRouter + QueryClientProvider + a single Suspense boundary,
 * then hands off to lazy-loaded route trees:
 *
 *   /admin/*   → AdminApp chunk (desktop, behind auth)
 *   /o/*       → GuestApp chunk (mobile-first, cookie session via PIN)
 *   /staff/*   → StaffApp chunk (tablet, magic-link auth for waiters)
 *   /          → redirect to /admin/
 *
 * Each route tree is a dynamic import, so Vite splits them into separate
 * JS chunks. A guest scanning their QR loads only the guest chunk; an
 * admin logging in loads only the admin chunk. Vendor deps (react,
 * react-router, react-query, date-fns) live in their own cacheable chunks.
 */

import React, { Suspense, lazy } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import './styles/index.css';
import { CustomHostBootstrap } from './CustomHostBootstrap.js';

const AdminApp = lazy(() => import('./admin/App.js'));
const GuestApp = lazy(() => import('./guest/App.js'));
const HostApp = lazy(() => import('./host/App.js'));
const StaffApp = lazy(() => import('./staff/App.js'));
const ShareDishPage = lazy(() => import('./guest/pages/ShareDish.js'));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      refetchOnWindowFocus: true,
      retry: 1,
    },
  },
});

function AppShellLoading() {
  return (
    <div className="min-h-screen grid place-items-center">
      <p className="text-muted">Wird geladen...</p>
    </div>
  );
}

const rootEl = document.getElementById('root');
if (!rootEl) throw new Error('root element missing');

ReactDOM.createRoot(rootEl).render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Suspense fallback={<AppShellLoading />}>
          <Routes>
            <Route path="/admin/*" element={<AdminApp />} />
            <Route path="/o/*" element={<GuestApp />} />
            <Route path="/staff/*" element={<StaffApp />} />
            <Route path="/host/*" element={<HostApp />} />
            <Route path="/" element={<CustomHostBootstrap />} />
            <Route path="/s/:shareId" element={<ShareDishPage />} />
            <Route path="*" element={<Navigate to="/admin" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </QueryClientProvider>
  </React.StrictMode>
);
